import React, { useEffect, useState } from "react";
import { L as Link } from "../lib/i18n";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { getRecentViews, pruneRecentViews } from "../lib/recentViews";
import CarCard from "./CarCard";
import { History, ArrowRight } from "lucide-react";

/**
 * Renders the user's last viewed listings.
 *
 * Sources, in priority order:
 *   1. Logged-in user → backend `/api/recent-views` (with cross-device sync).
 *   2. Fallback → localStorage (`carhive_recent_views_v1`).
 *
 * Phase 72 — auto-removes deleted / hidden listings on mount:
 *   • Guest path: batch-check via /api/listings/exist and prune localStorage.
 *   • Auth path: backend already filters + also deletes stale rows server-side.
 *
 * Optional `excludeId` skips the current listing (used on /listings/{id}).
 * Renders nothing if there's nothing relevant to show.
 */
export default function RecentlyViewed({ excludeId, limit = 6, title = "Kürzlich angesehen", showViewAllLink = true }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    const local = getRecentViews({ excludeId });

    if (user) {
      // Auth path — backend already returns only active listings AND deletes
      // stale rows server-side (Phase 72). Sync local storage to match.
      api.recentViews()
        .then((r) => {
          if (!alive) return;
          const server = Array.isArray(r?.items) ? r.items : [];
          if (server.length > 0) {
            pruneRecentViews(server.map((s) => s.id));
          }
          const filtered = excludeId ? server.filter((x) => x.id !== excludeId) : server;
          setItems(filtered.length > 0 ? filtered : local);
          setLoaded(true);
        })
        .catch(() => { if (alive) { setItems(local); setLoaded(true); } });
      return () => { alive = false; };
    }

    // Guest path — validate localStorage against server so deleted listings
    // vanish from "Kürzlich angesehen" on next page load.
    if (local.length === 0) {
      setItems([]);
      setLoaded(true);
      return () => { alive = false; };
    }
    api.listingsExist(local.map((e) => e.id))
      .then((r) => {
        if (!alive) return;
        const activeIds = Array.isArray(r?.active_ids) ? r.active_ids : [];
        pruneRecentViews(activeIds);
        const pruned = local.filter((e) => activeIds.includes(e.id));
        setItems(pruned);
        setLoaded(true);
      })
      .catch(() => {
        // Network failure — fail-open with the unpruned local list.
        if (!alive) return;
        setItems(local);
        setLoaded(true);
      });
    return () => { alive = false; };
  }, [user, excludeId]);

  if (!loaded || items.length === 0) return null;
  const visible = items.slice(0, limit);

  return (
    <section className="mt-14" data-testid="recently-viewed-section">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.25em] text-red-500">
            <History className="h-3 w-3" /> Verlauf
          </p>
          <h2 className="mt-1 font-display text-xl font-semibold text-white sm:text-2xl">{title}</h2>
        </div>
        {showViewAllLink && (
          <Link
            to="/listings"
            className="hidden text-xs text-zinc-400 hover:text-red-400 sm:inline-flex sm:items-center sm:gap-1"
            data-testid="recently-viewed-view-all"
          >
            Weiter ansehen <ArrowRight className="h-3 w-3" />
          </Link>
        )}
      </div>

      {/* Mobile: horizontal scroll snap */}
      <div className="mt-6 -mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-3 sm:hidden" data-testid="recently-viewed-mobile">
        {visible.map((it) => (
          <div key={it.id} className="w-[78%] shrink-0 snap-start">
            <CarCard car={it} />
          </div>
        ))}
      </div>

      {/* Desktop / tablet: grid */}
      <div className="mt-6 hidden gap-4 sm:grid sm:grid-cols-2 lg:grid-cols-3" data-testid="recently-viewed-grid">
        {visible.map((it) => (
          <CarCard key={it.id} car={it} />
        ))}
      </div>
    </section>
  );
}
